import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import FilterPanel from './components/ui/FilterPanel';
import ListingsGrid from './components/listings/ListingsGrid';
import Favorites from './components/pages/Favorites';
import Profile from './components/pages/Profile';
import { fetchListings } from './services/api';
import { FavoritesProvider } from './context/FavoritesContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LandingPage from './components/landing/LandingPage/LandingPage';
import AboutPage from './components/about/AboutPage';
import { Box, IconButton, Avatar, Menu, MenuItem, Divider, Chip, Button, AppBar, Toolbar, Container } from '@mui/material';
import { Star, AccountCircle, AdminPanelSettings, Logout, Dashboard as DashboardIcon, Business, Favorite } from '@mui/icons-material';
import { useAuth } from './contexts/AuthContext';
import { RoleProvider, ROLES, useRole } from './contexts/RoleContext';
import { ThemeModeProvider } from './contexts/ThemeContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/pages/Dashboard';
import RoleRoute from './components/auth/RoleRoute';
import AuthRoute from './components/auth/AuthRoute';
import Unauthorized from './components/auth/Unauthorized';
import AdminDashboard from './components/admin/AdminDashboard';
import { OnboardingProvider } from './contexts/OnboardingContext';
import OnboardingCards from './components/onboarding/OnboardingCards';
import ThemeToggle from './components/common/ThemeToggle';
import EnhancedDashboard from './components/dashboard/EnhancedDashboard';
import NextAdminDashboard from './components/dashboard/NextAdminDashboard';
import EnhancedAdminDashboard from './components/admin/EnhancedAdminDashboard';
import EnhancedProfile from './components/profile/EnhancedProfile';
import NJCountiesMap from './components/pages/NJCountiesMap';

// Footer Pages
import { 
  FeaturedProperties, 
  ResidentialProperties, 
  CommercialProperties,
  InvestmentFunds,
  ForeclosureGuide,
  InvestmentBasics,
  ROICalculator, 
  PrivacyPolicy,
  FAQ
} from './components/footer-pages';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#1B5E20', // Dark green from logo
      light: '#4C8C4A',
      dark: '#003300',
    },
    secondary: {
      main: '#88c5a6', // Light green from logo
      light: '#baf8d4',
      dark: '#59947a',
    },
    background: {
      default: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// Public pages built on the DS360 site layout render their own header and footer.
const SITE_LAYOUT_PATHS = ['/', '/about'];

// Legacy app header, shown on every page that doesn't use the site layout
const ConditionalHeader = () => {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const { hasRole, isPro } = useRole();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const usesSiteLayout = SITE_LAYOUT_PATHS.includes(location.pathname);

  const handleMenu = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  
  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  if (usesSiteLayout) {
    return null;
  }

  const isProUser = isPro();

  return (
    <AppBar 
      position="static" 
      elevation={3}
      sx={{ 
        backgroundColor: '#96ffa8',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar 
          disableGutters 
          sx={{ 
            py: { xs: 1, md: 2 },
            minHeight: { xs: '64px', md: '80px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {/* Logo Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, maxWidth: 'fit-content' }}>
            <Button
              component={Link}
              to="/"
              sx={{
                textAlign: 'left',
                textTransform: 'none',
                color: 'black',
                '&:hover': { backgroundColor: 'transparent' },
                p: 0
              }}
            >
              <Box>
                <Box sx={{ fontSize: { xs: '1.5rem', md: '1.875rem' }, fontWeight: 700, lineHeight: 1.2 }}>
                  Foreclosure Dealfinder
                </Box>
                <Box sx={{ fontSize: '0.875rem', color: '#374151', mt: 0.5 }}>
                  Find investment opportunities in foreclosure properties
                </Box>
              </Box>
            </Button>
          </Box>

          {/* Navigation Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentUser ? (
              <>
                {/* Theme Toggle */}
                <ThemeToggle />
                
                {/* Navigation Buttons - Only show for admins */}
                {hasRole(ROLES.ADMIN) && (
                  <>
                    <Button
                      component={Link}
                      to="/dashboard"
                      startIcon={<DashboardIcon />}
                      sx={{
                        color: '#1B5E20',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: '#82e694'
                        }
                      }}
                    >
                      Dashboard
                    </Button>
                    
                    <Button
                      component={Link}
                      to="/properties"
                      startIcon={<Business />}
                      sx={{
                        color: '#1B5E20',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: '#82e694'
                        }
                      }}
                    >
                      Listings
                    </Button>
                    
                    <Button
                      component={Link}
                      to="/favorites"
                      startIcon={<Favorite />}
                      sx={{
                        color: '#1B5E20',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: '#82e694'
                        }
                      }}
                    >
                      Favorites
                    </Button>
                  </>
                )}

                {/* PRO Badge */}
                {currentUser && isProUser && (
                  <Chip
                    icon={<Star />}
                    label="PRO"
                    sx={{
                      backgroundColor: 'gold',
                      color: 'black',
                      fontWeight: 'bold',
                      ml: 1,
                      '& .MuiChip-icon': {
                        color: 'black'
                      }
                    }}
                    size="small"
                  />
                )}

                {/* Profile Avatar Menu */}
                <Box sx={{ ml: 2, pl: 2, borderLeft: '1px solid #82e694' }}>
                  <IconButton 
                    onClick={handleMenu}
                    sx={{
                      color: '#1B5E20',
                      '&:hover': {
                        backgroundColor: '#82e694'
                      }
                    }}
                  >
                    <Avatar 
                      sx={{
                        bgcolor: hasRole(ROLES.ADMIN) ? '#ff4444' : '#1B5E20',
                        width: 35,
                        height: 35
                      }}
                    >
                      {currentUser.email[0].toUpperCase()}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        backgroundColor: '#f0fff3',
                        '& .MuiMenuItem-root': {
                          color: '#1B5E20'
                        }
                      }
                    }}
                  >
                    {hasRole(ROLES.ADMIN) && (
                      <>
                        <MenuItem onClick={handleClose} component={Link} to="/profile">
                          <AccountCircle sx={{ mr: 1 }} />
                          Profile
                        </MenuItem>
                        <MenuItem onClick={handleClose} component={Link} to="/admin">
                          <AdminPanelSettings sx={{ mr: 1 }} />
                          Admin Panel
                        </MenuItem>
                        <Divider />
                      </>
                    )}
                    <MenuItem onClick={handleLogout} sx={{ color: '#cc0000' }}>
                      <Logout sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>
              </>
            ) : (
              <Button
                component={Link}
                to="/login"
                variant="contained"
                sx={{
                  backgroundColor: '#1B5E20',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#0d3f16'
                  }
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Properties page component
const PropertiesPage = ({ listings, loading, pagination, onPageChange, onFilterChange }) => {
  const location = useLocation();
  
  // Force a re-render effect when this component mounts - but only once
  useEffect(() => {
    // If no listings and not loading, trigger a page change to force data load
    if (!listings?.length && !loading) {
      onPageChange(1, 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount to prevent infinite loops

  return (
    <Box sx={{ maxWidth: '7xl', mx: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
      <FilterPanel onFilterChange={onFilterChange} />
      <ListingsGrid 
        listings={listings} 
        loading={loading} 
        pagination={pagination} 
        onPageChange={onPageChange}
      />
    </Box>
  );
};

function App() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: 'NJ' }); // Default to NJ
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10, // Default to 10 rows per page
  });
  const location = useLocation();

  // Reset state when navigating away from properties page
  useEffect(() => {
    if (location.pathname !== '/properties') {
      // Clear listings and reset state when not on properties page
      setListings([]);
      setLoading(true);
    }
  }, [location.pathname]);

  // Load listings when filters, pagination changes, or when navigating to properties page
  useEffect(() => {
    // Only load listings if we're on the properties page
    if (location.pathname !== '/properties') {
      return;
    }

    const loadListings = async () => {
      setLoading(true);
      
      try {
        // Combine filters with pagination
        const queryParams = {
          ...filters,
          page: pagination.currentPage,
          limit: pagination.pageSize,
        };
        
        const result = await fetchListings(queryParams);
        
        // Log the first item to see the format
        // Removed noisy debug logging
        
        // Process data to ensure proper formatting
        const processedData = result.data.map(item => {
          // Convert dates to strings in the correct format
          let processedItem = { ...item };
          
          // Handle upset values - remove any formatting and ensure it's a clean value
          if (item.upset_value) {
            // Remove any existing formatting and convert to number
            const cleanValue = String(item.upset_value).replace(/[$,]/g, '');
            processedItem.upset_value = cleanValue;
          }
                    
          return processedItem;
        });
        
        setListings(processedData);
        
        // Only update pagination metadata (not currentPage/pageSize) to prevent loops
        setPagination(prev => ({
          ...prev, // Keep current page and page size
          totalPages: result.pagination.totalPages,
          totalCount: result.pagination.totalCount,
        }));
      } catch (error) {
        console.error('Error loading listings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadListings();
  }, [filters, pagination.currentPage, pagination.pageSize, location.pathname]);

  // Handle filter changes - memoized to prevent infinite loops
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
    }));
  }, []);

  // Handle page changes - memoized to prevent infinite loops
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage,
      // Update page size if provided
      ...(newPageSize ? { pageSize: newPageSize } : {})
    }));
  }, []);

  return (
    <ThemeModeProvider>
      <CssBaseline />
      <RoleProvider>
        <FeatureFlagProvider>
          <OnboardingProvider>
            <FavoritesProvider>
              <Box sx={{ minHeight: '100vh' }}>
                <ConditionalHeader />
                
                <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                
                {/* Auth routes - protected from authenticated users */}
                <Route
                  path="/login"
                  element={
                    <AuthRoute>
                      <Login />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <AuthRoute>
                      <Signup />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <AuthRoute>
                      <ForgotPassword />
                    </AuthRoute>
                  }
                />

                {/* Onboarding route */}
                <Route
                  path="/onboarding"
                  element={
                    <RoleRoute>
                      <OnboardingCards />
                    </RoleRoute>
                  }
                />
                
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Protected routes with role-based access */}
                <Route
                  path="/dashboard"
                  element={
                    <RoleRoute requiredPermission="view_properties">
                      <NextAdminDashboard />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/dashboard/enhanced"
                  element={
                    <RoleRoute requiredPermission="view_properties">
                      <EnhancedDashboard />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/dashboard/classic"
                  element={
                    <RoleRoute requiredPermission="view_properties">
                      <Dashboard />
                    </RoleRoute>
                  }
                />
                <Route 
                  path="/properties" 
                  element={
                    <RoleRoute requiredPermission="view_properties">
                      <PropertiesPage 
                        listings={listings}
                        loading={loading}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onFilterChange={handleFilterChange}
                      />
                    </RoleRoute>
                  } 
                />
                <Route 
                  path="/favorites" 
                  element={
                    <RoleRoute requiredPermission="manage_favorites">
                      <Box sx={{ maxWidth: '7xl', mx: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
                        <Favorites />
                      </Box>
                    </RoleRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <RoleRoute>
                      <EnhancedProfile />
                    </RoleRoute>
                  } 
                />
                <Route 
                  path="/profile/classic" 
                  element={
                    <RoleRoute>
                      <Profile />
                    </RoleRoute>
                  } 
                />

                {/* Admin routes */}
                <Route
                  path="/admin"
                  element={
                    <RoleRoute requiredRole={ROLES.ADMIN}>
                      <EnhancedAdminDashboard />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin/classic"
                  element={
                    <RoleRoute requiredRole={ROLES.ADMIN}>
                      <AdminDashboard />
                    </RoleRoute>
                  }
                />

                {/* Footer Pages - Public Routes */}
                {/* Investment Pages */}
                <Route path="/featured-properties" element={<FeaturedProperties />} />
                <Route path="/residential-properties" element={<ResidentialProperties />} />
                <Route path="/commercial-properties" element={<CommercialProperties />} />
                <Route path="/investment-funds" element={<InvestmentFunds />} />
                
                {/* Education Pages */}
                <Route path="/foreclosure-guide" element={<ForeclosureGuide />} />
                <Route path="/investment-basics" element={<InvestmentBasics />} />
                
                {/* Tools Pages */}
                <Route path="/roi-calculator" element={<ROICalculator />} />
                
                {/* Company Pages */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/faq" element={<FAQ />} />
                
                {/* NJ Counties Map - Protected route for non-admin users */}
                <Route
                  path="/nj-counties"
                  element={
                    <RoleRoute>
                      <NJCountiesMap />
                    </RoleRoute>
                  }
                />
              </Routes>
              </Box>
            </FavoritesProvider>
          </OnboardingProvider>
        </FeatureFlagProvider>
      </RoleProvider>
    </ThemeModeProvider>
  );
}

export default App; 